/**
 * Moment scenes: the broadcast package for each director chapter. Pure, built ONLY from the
 * director response (verified moments/asOf/call) and the bounded catch-up plays — never from
 * archive finals, invented trajectories or tracking. Everything here is unit-tested.
 *
 * Perspective is a framing of the same facts: the same comeback reads "Hawks erase 14" for an
 * Atlanta fan and "The 14-point lead is gone" for a Knicks fan. Scores and outcomes never differ.
 */
import { directorClock, GameEventSchema, team, type DirectorCallEvidence, type DirectorMoment, type DirectorResponse, type DirectorVisual, type GameEvent } from '@clutch/core';
import { loc, pick, normalizeLanguage, isChineseLanguage } from './i18n';
import { locatedShot, momentVisual } from './moment-presentation';
import { historyReliable } from './perspective';

export type Side = 'home' | 'away';
export type Lens = { followed?: readonly string[]; familiarity?: 'new' | 'regular' | 'expert'; who?: string };

export type Relevance = { kind: 'call' | 'team' | 'player' | 'reaction' | 'key'; tone: 'for' | 'against' | 'neutral'; label: string };
/** Margins are signed home − away (positive = home leads). */
export type LeadSwing = { from: number; to: number; fromClock?: string; toClock: string };
export type ShotMark = { eventId: string; seq: number; x: number; y: number; made: boolean; points?: number; name?: string; side?: Side; clock: string };
export type KeyPlay = { seq: number; clock: string; side?: Side; text: string; away: number; home: number; scoring: boolean; decisive: boolean };
export type PlayerIntro = { personId: number; name: string; side?: Side; tricode?: string; points?: number };
export type MomentScene = {
  momentId: string;
  kind: DirectorMoment['kind'];
  chapter: number;
  chapters: number;
  relevance: Relevance;
  title: string;
  /** Immediate context: when + situation ("Q3 6:56 · Knicks up 14"). */
  situation: string;
  /** Why it matters, in this fan's perspective. */
  why: string;
  lead?: LeadSwing;
  shot?: ShotMark;
  plays: KeyPlay[];
  player?: PlayerIntro;
  explainer?: string;
  call?: { pct: number; team: string; code: string; clock: string; result: DirectorCallEvidence['result'] };
  climax: boolean;
};

const teamName = (code: string, l: string) => (isChineseLanguage(l) ? ZH_TEAM[code] ?? team(code).name : team(code).name);
const margin = (e: { homeScore: number; awayScore: number }) => e.homeScore - e.awayScore;
const lastName = (n: string) => {
  const parts = n.trim().split(/\s+/);
  return /^(?:Jr\.?|Sr\.?|II|III|IV)$/i.test(parts.at(-1) ?? '') ? parts.slice(-2).join(' ') : parts.at(-1) ?? n;
};

/** Scene helpers may receive a full replay ledger. Reject foreign, future, and ambiguous events. */
export function boundedScenePlays(d: Pick<DirectorResponse, 'game' | 'asOf'>, plays: readonly GameEvent[], cutoff = d.asOf.seq): GameEvent[] {
  const bySeq = new Map<number, GameEvent>();
  const ambiguous = new Set<number>();
  for (const raw of plays) {
    const parsed = GameEventSchema.safeParse(raw);
    if (!parsed.success) continue;
    const p = parsed.data;
    if (p.gameId !== d.game.gameId || !p.eventId.startsWith(`${d.game.gameId}:`) || p.seq > Math.min(cutoff, d.asOf.seq)) continue;
    const prior = bySeq.get(p.seq);
    if (prior && JSON.stringify(prior) !== JSON.stringify(p)) ambiguous.add(p.seq);
    bySeq.set(p.seq, p);
  }
  return [...bySeq.values()].filter((p) => !ambiguous.has(p.seq)).sort((a, b) => a.seq - b.seq);
}

/** The side this fan is on: their call, else a followed team playing in this game. */
export function fanSide(d: Pick<DirectorResponse, 'game' | 'call'>, followed: readonly string[] = []): Side | undefined {
  const pickTeam = d.call?.chosenTeam ?? followed.find((t) => t === d.game.home.tricode || t === d.game.away.tricode);
  if (!pickTeam) return undefined;
  return pickTeam === d.game.home.tricode ? 'home' : pickTeam === d.game.away.tricode ? 'away' : undefined;
}

export const isClimax = (m: Pick<DirectorMoment, 'kind' | 'reasons'>) =>
  m.kind === 'finish' || m.reasons.some((r) => r === 'required_climax' || r === 'closing_lead_change' || r === 'last_observed_period_play');

function indexPlays(plays: readonly GameEvent[]) {
  const byId = new Map<string, GameEvent>();
  for (const p of plays) byId.set(p.eventId, p);
  return byId;
}

function sideTricode(d: Pick<DirectorResponse, 'game'>, s: Side) {
  return s === 'home' ? d.game.home.tricode : d.game.away.tricode;
}

/** "Knicks up 14" / "Tied 103–103" from a margin. */
export function situationText(d: Pick<DirectorResponse, 'game'>, m: number, away: number, home: number, l: string): string {
  if (m === 0) return pick({ en: `Tied ${away}–${home}`, hi: `${away}–${home} पर बराबरी`, zh: `${away}平` }, l);
  const leader = teamName(m > 0 ? d.game.home.tricode : d.game.away.tricode, l);
  const n = Math.abs(m);
  return pick({ en: `${leader} up ${n}`, hi: `${leader} ${n} से आगे`, zh: `${leader}领先${n}分` }, l);
}

type Tri = { en: string; hi: string; zh: string };
const OWN_TAG: Record<'call' | 'reaction' | 'player' | 'team', Tri> = {
  call: { en: 'Your call', hi: 'आपकी कॉल', zh: '你的预测' },
  reaction: { en: 'Your reaction', hi: 'आपका रिएक्शन', zh: '你的反应' },
  player: { en: 'Your player', hi: 'आपका खिलाड़ी', zh: '你的球员' },
  team: { en: 'Your team', hi: 'आपकी टीम', zh: '你的球队' },
};
const PUBLIC_TAG: Record<keyof typeof OWN_TAG, (a: string) => Tri> = {
  call: (a) => ({ en: `${a}’s call`, hi: `${a} की कॉल`, zh: `${a}的预测` }),
  reaction: (a) => ({ en: `${a}’s reaction`, hi: `${a} का रिएक्शन`, zh: `${a}的反应` }),
  player: (a) => ({ en: `${a}’s player`, hi: `${a} का खिलाड़ी`, zh: `${a}的球员` }),
  team: (a) => ({ en: `${a}’s team`, hi: `${a} की टीम`, zh: `${a}的球队` }),
};

export function relevanceFor(m: DirectorMoment, d: Pick<DirectorResponse, 'game' | 'call' | 'audience'>, side: Side | undefined, l: string, who?: string): Relevance {
  const author = who ?? d.audience?.author;
  const own = (k: 'call' | 'reaction' | 'player' | 'team') => pick(author ? PUBLIC_TAG[k](author) : OWN_TAG[k], l);
  if (m.kind === 'call' || m.lens === 'call') return { kind: 'call', tone: 'for', label: own('call') };
  if (m.lens === 'reaction') return { kind: 'reaction', tone: 'for', label: own('reaction') };
  if (m.lens === 'player') return { kind: 'player', tone: 'for', label: own('player') };
  if (side) {
    const toward = Math.sign(margin(m)) === (side === 'home' ? 1 : -1);
    return { kind: 'team', tone: toward ? 'for' : 'against', label: own('team') };
  }
  return { kind: 'key', tone: 'neutral', label: pick({ en: 'Key moment', hi: 'अहम पल', zh: '关键时刻' }, l) };
}

/** Plays tied to a moment, by its evidence IDs (only those inside the bounded ledger). */
function evidence(m: DirectorMoment, byId: Map<string, GameEvent>) {
  return m.eventIds.map((id) => byId.get(id)).filter((e): e is GameEvent => !!e && e.seq <= m.seq).sort((a, b) => a.seq - b.seq);
}

function previous(plays: readonly GameEvent[], seq: number) {
  let prev: GameEvent | undefined;
  for (const p of plays) {
    if (p.seq >= seq) break;
    prev = p;
  }
  return prev?.seq === seq - 1 ? prev : undefined;
}

export function leadSwing(m: DirectorMoment, plays: readonly GameEvent[], l = 'en'): LeadSwing | undefined {
  const sorted = [...plays].sort((a, b) => a.seq - b.seq);
  const ev = evidence(m, indexPlays(sorted));
  const to = margin(m);
  const toClock = directorClock(m.period, m.clockSec, normalizeLanguage(l));
  if (m.kind === 'call') {
    const before = ev[0] ? previous(sorted, ev[0].seq) : undefined;
    return before ? { from: margin(before), to, fromClock: directorClock(before.period, before.clockSec, normalizeLanguage(l)), toClock } : { from: to, to, toClock };
  }
  if (m.kind === 'reaction') return undefined;
  const start = m.kind === 'comeback' || m.kind === 'run' ? ev[0] : ev[0] ? previous(sorted, ev[0].seq) : undefined;
  if (!start || start.seq > m.seq) return undefined;
  return { from: margin(start), to, fromClock: directorClock(start.period, start.clockSec, normalizeLanguage(l)), toClock };
}

export function keyShot(m: DirectorMoment, plays: readonly GameEvent[], l = 'en'): ShotMark | undefined {
  const ev = evidence(m, indexPlays(plays));
  const shots = ev.filter((e) => locatedShot(e) && typeof e.made === 'boolean');
  const s = shots.at(-1);
  if (!s) return undefined;
  return {
    eventId: s.eventId, seq: s.seq,
    x: s.x!, y: s.y!, made: !!s.made,
    ...(s.points !== undefined ? { points: s.points } : {}),
    ...(s.playerName ? { name: s.playerName } : {}),
    ...(s.side ? { side: s.side } : {}),
    clock: directorClock(s.period, s.clockSec, normalizeLanguage(l)),
  };
}

/** A player's running total at (or before) seq, only from the feed's own running totals. */
function pointsAt(plays: readonly GameEvent[], personId: number, seq: number): number | undefined {
  let pts: number | undefined;
  for (const p of plays) if (p.seq <= seq && p.personId === personId && typeof p.playerPoints === 'number') pts = p.playerPoints;
  return pts;
}

export function playerFor(m: DirectorMoment, d: Pick<DirectorResponse, 'game'>, plays: readonly GameEvent[], visual?: DirectorVisual): PlayerIntro | undefined {
  const ev = evidence(m, indexPlays(plays));
  const shooter = [...ev].reverse().find((e) => e.personId && e.playerName && (e.type === 'shot' || e.type === 'freethrow'));
  const personId = m.player?.personId ?? shooter?.personId;
  const name = m.player?.name ?? shooter?.playerName;
  if (!personId || !name) return undefined;
  const side = visual?.personId === personId ? visual.side : plays.find((p) => p.seq <= m.seq && p.personId === personId && p.side)?.side;
  const points = pointsAt(plays, personId, m.seq);
  return { personId, name, ...(side ? { side, tricode: sideTricode(d, side) } : {}), ...(points !== undefined ? { points } : {}) };
}

function playText(p: GameEvent, d: Pick<DirectorResponse, 'game'>, l: string): string | undefined {
  const who = p.playerName ? lastName(p.playerName) : p.side ? teamName(sideTricode(d, p.side), l) : undefined;
  if (!who) return undefined;
  switch (p.type) {
    case 'shot':
      if (p.made) return p.points === 3 ? pick({ en: `${who} hits a three`, hi: `${who} का तीन-पॉइंटर`, zh: `${who} 命中三分` }, l) : pick({ en: `${who} scores`, hi: `${who} ने स्कोर किया`, zh: `${who} 得分` }, l);
      if (p.made !== false) return undefined;
      return pick({ en: `${who} misses`, hi: `${who} का शॉट चूका`, zh: `${who} 投篮不中` }, l);
    case 'freethrow':
      if (p.made === undefined) return undefined;
      return p.made ? pick({ en: `${who} makes a free throw`, hi: `${who} का फ़्री थ्रो सफल`, zh: `${who} 罚球命中` }, l) : pick({ en: `${who} misses a FT`, hi: `${who} का फ़्री थ्रो चूका`, zh: `${who} 罚球不中` }, l);
    case 'block':
      return pick({ en: `${who} blocks it`, hi: `${who} ने ब्लॉक किया`, zh: `${who} 盖帽` }, l);
    case 'steal':
      return pick({ en: `${who} steals it`, hi: `${who} ने गेंद छीनी`, zh: `${who} 抢断` }, l);
    case 'turnover':
      return pick({ en: `${who} turns it over`, hi: `${who} से टर्नओवर`, zh: `${who} 失误` }, l);
    default:
      return undefined;
  }
}

/**
 * The decisive sequence: verified plays from a range, reduced to what a fan needs to follow it
 * (shots, free throws, blocks, steals, turnovers). The last scoring play and the final shot are
 * marked decisive.
 */
export function sequencePlays(d: Pick<DirectorResponse, 'game'>, plays: readonly GameEvent[], fromSeq: number, toSeq: number, l: string, max = 6): KeyPlay[] {
  const out: KeyPlay[] = [];
  let lastFt: { key: string; n: number; made: number } | undefined;
  for (const p of [...plays].sort((a, b) => a.seq - b.seq)) {
    if (p.gameId !== d.game.gameId || p.seq < fromSeq || p.seq > toSeq) continue;
    const text = playText(p, d, l);
    if (!text) continue;
    // A trip to the line reads as one beat: "McCollum misses 2 FTs" / "makes 1 of 2".
    const ftKey = p.type === 'freethrow' ? `${p.personId ?? p.playerName}|${p.period}|${p.clockSec}` : undefined;
    const prev = out.at(-1);
    if (ftKey && lastFt?.key === ftKey && prev) {
      lastFt.n++;
      lastFt.made += p.made ? 1 : 0;
      const who = p.playerName ? lastName(p.playerName) : '';
      const { n, made } = lastFt;
      prev.text = made === 0
        ? pick({ en: `${who} misses ${n} FTs`, hi: `${who} के ${n} फ़्री थ्रो चूके`, zh: `${who} ${n}罚不中` }, l)
        : pick({ en: `${who} makes ${made} of ${n} FTs`, hi: `${who} ने ${n} में से ${made} फ़्री थ्रो डाले`, zh: `${who} ${n}罚${made}中` }, l);
      Object.assign(prev, { seq: p.seq, away: p.awayScore, home: p.homeScore, scoring: prev.scoring || (!!p.made && (p.points ?? 0) > 0) });
      continue;
    }
    lastFt = ftKey ? { key: ftKey, n: 1, made: p.made ? 1 : 0 } : undefined;
    out.push({ seq: p.seq, clock: directorClock(p.period, p.clockSec, normalizeLanguage(l)), ...(p.side ? { side: p.side } : {}), text, away: p.awayScore, home: p.homeScore, scoring: !!p.made && (p.points ?? 0) > 0, decisive: false });
  }
  const kept = out.slice(-max);
  const lastScore = [...kept].reverse().find((k) => k.scoring);
  if (lastScore) lastScore.decisive = true;
  const lastShot = kept.at(-1);
  if (lastShot) lastShot.decisive = true;
  return kept;
}

function deficit(m: DirectorMoment, plays: readonly GameEvent[]): number | undefined {
  const start = evidence(m, indexPlays(plays))[0];
  if (!start) return undefined;
  const n = Math.abs(margin(start));
  return n > 0 ? n : undefined;
}

function comebackSide(m: DirectorMoment, plays: readonly GameEvent[]): Side | undefined {
  const start = evidence(m, indexPlays(plays))[0];
  if (!start || margin(start) === 0) return undefined;
  const side = margin(start) > 0 ? 'away' : 'home';
  return margin(m) * (side === 'home' ? 1 : -1) >= 0 ? side : undefined;
}

/** Perspective chapter title. Falls back to the server's localized title when facts are missing. */
export function chapterTitle(m: DirectorMoment, d: Pick<DirectorResponse, 'game' | 'call' | 'audience' | 'asOf'>, side: Side | undefined, plays: readonly GameEvent[], l: string, who?: string): string {
  const author = who ?? d.audience?.author;
  if (m.kind === 'call' && d.call) {
    const t = teamName(d.call.chosenTeam, l);
    return author ? pick({ en: `${author} backs the ${t}`, hi: `${author} ने ${t} को चुना`, zh: `${author}看好${t}` }, l) : pick({ en: `You back the ${t}`, hi: `आपने ${t} को चुना`, zh: `你看好${t}` }, l);
  }
  if (m.kind === 'comeback') {
    const n = deficit(m, plays);
    const subject = comebackSide(m, plays);
    if (n && subject) {
      const t = teamName(sideTricode(d, subject), l);
      if (side && side !== subject) return pick({ en: `The ${n}-point lead is gone`, hi: `${n} अंकों की बढ़त गई`, zh: `${n}分领先化为乌有` }, l);
      return pick({ en: `${t} erase ${n}`, hi: `${t} ने ${n} अंकों का फ़ासला मिटाया`, zh: `${t}抹平${n}分差距` }, l);
    }
  }
  if (isClimax(m) && d.asOf.status === 'final') {
    const byId = indexPlays(plays);
    const last = evidence(m, byId).at(-1);
    const firstEv = evidence(m, byId)[0];
    const secs = firstEv && firstEv.period >= 4 && firstEv.clockSec <= 120 ? Math.ceil(firstEv.clockSec) : undefined;
    if (side && last?.side && last.type === 'shot') {
      return last.side === side
        ? pick({ en: 'One last shot', hi: 'आख़िरी शॉट', zh: '最后一投' }, l)
        : pick({ en: 'Survive the last shot', hi: 'आख़िरी शॉट झेलना है', zh: '顶住最后一投' }, l);
    }
    if (secs) return pick({ en: `The last ${secs} seconds`, hi: `आख़िरी ${secs} सेकंड`, zh: `最后${secs}秒` }, l);
  }
  return loc(m.title as Partial<Record<string, string>>, l);
}

function whyText(m: DirectorMoment, d: Pick<DirectorResponse, 'game' | 'call' | 'audience' | 'asOf'>, side: Side | undefined, plays: readonly GameEvent[], l: string, who?: string): string {
  const author = who ?? d.audience?.author;
  if (m.kind === 'call') {
    const lockPlay = d.call?.lockedAtEventId ? plays.find((p) => p.eventId === d.call!.lockedAtEventId) : undefined;
    const before = lockPlay?.type === 'shot' && lockPlay.made && lockPlay.playerName
      ? pick({ en: `${lastName(lockPlay.playerName)}’s bucket had just landed. `, hi: `${lastName(lockPlay.playerName)} ने अभी-अभी अंक बनाए थे। `, zh: `${lastName(lockPlay.playerName)}刚刚得分。` }, l)
      : '';
    return before + (author ? pick({ en: `${author} committed here.`, hi: `${author} ने यहीं कॉल लगाई।`, zh: `${author}在这里做出了预测。` }, l) : pick({ en: 'This is where you committed.', hi: 'यहीं आपने अपनी कॉल लगाई।', zh: '你就在这一刻做出了预测。' }, l));
  }
  if (m.kind === 'comeback') {
    const start = evidence(m, indexPlays(plays))[0];
    const n = deficit(m, plays);
    const subject = comebackSide(m, plays);
    if (start && n && subject) {
      const at = directorClock(start.period, start.clockSec, normalizeLanguage(l));
      if (side && side !== subject) return author ? pick({ en: `${author}’s team led by ${n} at ${at}.`, hi: `${at} पर ${author} की टीम ${n} से आगे थी।`, zh: `${at}时，${author}的球队领先${n}分。` }, l) : pick({ en: `Your team led by ${n} at ${at}.`, hi: `${at} पर आपकी टीम ${n} से आगे थी।`, zh: `${at}时，你的球队领先${n}分。` }, l);
      if (side === subject) return author ? pick({ en: `${author}’s team was down ${n} at ${at}.`, hi: `${at} पर ${author} की टीम ${n} से पीछे थी।`, zh: `${at}时，${author}的球队落后${n}分。` }, l) : pick({ en: `Your team was down ${n} at ${at}.`, hi: `${at} पर आपकी टीम ${n} से पीछे थी।`, zh: `${at}时，你的球队落后${n}分。` }, l);
      const t = teamName(sideTricode(d, subject), l);
      return pick({ en: `${t} trailed by ${n} at ${at}.`, hi: `${at} पर ${t} ${n} से पीछे थे।`, zh: `${at}时，${t}落后${n}分。` }, l);
    }
  }
  if (isClimax(m)) return d.asOf.status === 'final'
    ? pick({ en: 'Down to the final possessions.', hi: 'अब बस आख़िरी कुछ पज़ेशन।', zh: '比赛进入最后几个回合。' }, l)
    : pick({ en: 'The latest stretch at this cutoff.', hi: 'इस अपडेट तक के हाल के प्ले।', zh: '截至此刻的最新回合。' }, l);
  if (m.kind === 'lead_change') return pick({ en: 'The lead flips.', hi: 'बढ़त पलट गई।', zh: '领先易主。' }, l);
  if (m.kind === 'run') return pick({ en: 'One team can’t stop scoring.', hi: 'एक टीम लगातार अंक बना रही है।', zh: '一方连续得分。' }, l);
  if (m.kind === 'reaction') return author ? pick({ en: `${author} reacted here.`, hi: `${author} ने यहाँ रिएक्ट किया।`, zh: `${author}在这里做出了反应。` }, l) : pick({ en: 'You reacted here.', hi: 'यहाँ आपने रिएक्ट किया।', zh: '你在这里做出了反应。' }, l);
  return pick({ en: 'A play that moved the game.', hi: 'एक प्ले जिसने मैच बदला।', zh: '改变比赛走势的一球。' }, l);
}

const EXPLAIN: Record<string, { en: string; hi: string; zh: string }> = {
  comeback: { en: 'Comeback: the team that was behind catches up, drawing level or going ahead.', hi: 'वापसी: पीछे चल रही टीम बराबरी करती है या आगे निकल जाती है।', zh: '追分：落后的一方追平或反超。' },
  lead_change: { en: 'Lead change: the trailing team goes in front.', hi: 'बढ़त बदली: पीछे चल रही टीम आगे निकल गई।', zh: '领先易主：落后一方实现反超。' },
  run: { en: 'A run: one team scores again and again while the other can’t answer.', hi: 'रन: एक टीम लगातार अंक बनाती है, दूसरी जवाब नहीं दे पाती।', zh: '得分潮：一方连续得分，另一方无法回应。' },
  climax: { en: 'A possession is one team’s turn with the ball. Each scoring chance can change a close game.', hi: 'पज़ेशन एक टीम के पास गेंद रहने का मौका है। करीबी मैच में हर स्कोर मायने रखता है।', zh: '一个回合就是一方持球进攻的机会。比分接近时，每次得分都很重要。' },
  score: { en: 'Shots inside the arc are worth 2, beyond it 3, free throws 1.', hi: 'आर्क के अंदर का शॉट 2 अंक, बाहर से 3, फ़्री थ्रो 1।', zh: '三分线内投篮得2分，线外3分，罚球1分。' },
  call: { en: 'A call: pick who wins at a key moment. Clutch saves the odds at that second.', hi: 'कॉल: अहम पल पर चुनिए कौन जीतेगा। Clutch उसी पल की संभावना सहेजता है।', zh: '预测：在关键时刻选出胜者，Clutch会保存那一刻的胜率。' },
};

export function explainerFor(m: DirectorMoment, l: string): string {
  const key = isClimax(m) ? 'climax' : m.kind === 'player' || m.kind === 'finish' ? 'score' : m.kind;
  return pick(EXPLAIN[key] ?? EXPLAIN.score!, l);
}

export function momentScene(d: DirectorResponse, momentId: string, plays: readonly GameEvent[], lens: Lens = {}, l: string = d.language): MomentScene | undefined {
  const idx = d.moments.findIndex((m) => m.momentId === momentId);
  const m = d.moments[idx];
  if (!m || m.seq > d.asOf.seq) return undefined;
  const bounded = boundedScenePlays(d, plays, m.seq);
  const sorted = historyReliable(d) ? bounded : [];
  const side = fanSide(d, lens.followed);
  const who = lens.who ?? d.audience?.author;
  const climax = isClimax(m);
  const ev = evidence(m, indexPlays(sorted));
  const first = ev[0];
  const lead = leadSwing(m, sorted, l);
  const visual = momentVisual(d, m, bounded);
  const shot = locatedShot(visual) && typeof visual.made === 'boolean'
    ? { eventId: visual.eventId, seq: visual.seq, x: visual.x, y: visual.y, made: visual.made, points: visual.points, side: visual.side, name: m.player?.name, clock: directorClock(visual.period, visual.clockSec, normalizeLanguage(l)) }
    : keyShot(m, sorted, l);
  const player = playerFor(m, d, sorted, visual);
  const familiarity = lens.familiarity ?? d.familiarity;
  return {
    momentId: m.momentId,
    kind: m.kind,
    chapter: idx + 1,
    chapters: d.moments.length,
    relevance: relevanceFor(m, d, side, l, who),
    title: chapterTitle(m, d, side, sorted, l, who),
    situation: `${directorClock(m.period, m.clockSec, normalizeLanguage(l))} · ${situationText(d, margin(m), m.awayScore, m.homeScore, l)}`,
    why: whyText(m, d, side, sorted, l, who),
    ...(lead ? { lead } : {}),
    ...(shot ? { shot } : {}),
    plays: climax && first ? sequencePlays(d, sorted, first.seq, m.seq, l, 7) : [],
    ...(player ? { player } : {}),
    ...(familiarity === 'new' ? { explainer: explainerFor(m, l) } : {}),
    ...(m.kind === 'call' && d.call ? { call: { pct: Math.round(d.call.pChoiceAtLock * 100), team: teamName(d.call.chosenTeam, l), code: d.call.chosenTeam, clock: directorClock(d.call.lockedAt.period, d.call.lockedAt.clockSec, normalizeLanguage(l)), result: d.call.result } } : {}),
    climax,
  };
}

/** Chapter cards for the intro: perspective titles + relevance, in story order. */
export function chapterList(d: DirectorResponse, plays: readonly GameEvent[], lens: Lens = {}, l: string = d.language) {
  return d.moments.map((m) => momentScene(d, m.momentId, plays, lens, l)!).filter(Boolean);
}

/** The ending's basketball resolution: the closing sequence that decided it (verified plays only). */
export function decisiveSequence(d: DirectorResponse, plays: readonly GameEvent[], l: string = d.language): KeyPlay[] {
  if (!historyReliable(d)) return [];
  const sorted = boundedScenePlays(d, plays);
  const climax = [...d.moments].reverse().find(isClimax);
  const byId = indexPlays(sorted);
  const start = climax ? evidence(climax, byId)[0] : undefined;
  if (start) return sequencePlays(d, sorted, start.seq, d.asOf.seq, l);
  const lastPeriod = sorted.at(-1)?.period;
  const tail = sorted.filter((p) => p.period === lastPeriod && p.clockSec <= 60);
  return tail.length ? sequencePlays(d, sorted, tail[0]!.seq, d.asOf.seq, l) : [];
}

/** The latest meaningful plays at the cutoff (for an unfinished story's last card). */
export function recentPlays(d: DirectorResponse, plays: readonly GameEvent[], l: string = d.language, max = 5): KeyPlay[] {
  if (!historyReliable(d)) return [];
  const sorted = boundedScenePlays(d, plays);
  const from = sorted.at(-40)?.seq ?? 0;
  const out = sequencePlays(d, sorted, from, d.asOf.seq, l, max);
  for (const k of out) k.decisive = false;
  return out;
}

/** "Who to watch": each side's top scorer at the cutoff, from the feed's running totals. */
export function whoToWatch(d: Pick<DirectorResponse, 'game' | 'asOf' | 'provenance'>, plays: readonly GameEvent[]): PlayerIntro[] {
  if (!historyReliable(d)) return [];
  const latest = new Map<number, PlayerIntro>();
  for (const p of boundedScenePlays(d, plays)) {
    if (p.seq > d.asOf.seq || !p.side || !p.personId || !p.playerName || typeof p.playerPoints !== 'number') continue;
    latest.set(p.personId, { personId: p.personId, name: p.playerName, side: p.side, tricode: sideTricode(d, p.side), points: p.playerPoints });
  }
  const best = new Map<Side, PlayerIntro>();
  for (const p of latest.values()) {
    const cur = best.get(p.side!);
    if (!cur || (p.points ?? 0) > (cur.points ?? 0)) best.set(p.side!, p);
  }
  return (['away', 'home'] as const).map((s) => best.get(s)).filter((x): x is PlayerIntro => !!x);
}

/** After the lock: what each side scored from the call to the cutoff (verified scores only). */
export function sinceCall(d: Pick<DirectorResponse, 'call' | 'moments' | 'asOf' | 'game'>): { away: number; home: number; lockMargin: number; finalMargin: number } | undefined {
  const c = d.call;
  if (!c) return undefined;
  const at = d.moments.find((m) => m.kind === 'call');
  if (!at || at.seq !== c.lockedAtSeq || at.seq > d.asOf.seq || at.homeScore > d.asOf.homeScore || at.awayScore > d.asOf.awayScore) return undefined;
  return { away: d.asOf.awayScore - at.awayScore, home: d.asOf.homeScore - at.homeScore, lockMargin: margin(at), finalMargin: margin(d.asOf) };
}

export const ZH_TEAM: Record<string, string> = {
  ATL: '老鹰', BOS: '凯尔特人', BKN: '篮网', CHA: '黄蜂', CHI: '公牛', CLE: '骑士', DAL: '独行侠', DEN: '掘金', DET: '活塞', GSW: '勇士',
  HOU: '火箭', IND: '步行者', LAC: '快船', LAL: '湖人', MEM: '灰熊', MIA: '热火', MIL: '雄鹿', MIN: '森林狼', NOP: '鹈鹕', NYK: '尼克斯',
  OKC: '雷霆', ORL: '魔术', PHI: '76人', PHX: '太阳', POR: '开拓者', SAC: '国王', SAS: '马刺', TOR: '猛龙', UTA: '爵士', WAS: '奇才',
};
export { teamName as localTeamName };
